import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";


const Tooltip = ({
  content,
  children,
  placement = "top",
  className = "",
  tooltipClassName = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);


  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipOffset = 8;
      
      let top, left;
      
      switch (placement) {
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + tooltipOffset;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - tooltipOffset;
          break;
        case "bottom":
          top = rect.bottom + tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
        case "top":
          top = rect.top - tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
        default:
          top = rect.top - tooltipOffset;
          left = rect.left + rect.width / 2;
      }
      
      setPosition({ top, left });
    }
  }, [isVisible, placement]);


  const getTransform = () => {
    switch (placement) {
      case "right":
        return "translateY(-50%)";
      case "left":
        return "translate(-100%, -50%)";
      case "bottom":
        return "translate(-50%, 0)";
      case "top":
        return "translate(-50%, -100%)";
      default:
        return "translate(-50%, -100%)";
    }
  };


  const getArrowStyles = () => {
    const baseArrow = "absolute w-0 h-0 border-solid";
    
    switch (placement) {
      case "top":
        return {
          className: `${baseArrow} border-t-[6px] border-t-[#3F3F46] border-x-[6px] border-x-transparent`,
          style: { bottom: "-6px", left: "50%", transform: "translateX(-50%)" }
        };
      case "bottom":
        return {
          className: `${baseArrow} border-b-[6px] border-b-[#3F3F46] border-x-[6px] border-x-transparent`,
          style: { top: "-6px", left: "50%", transform: "translateX(-50%)" }
        };
      case "left":
        return {
          className: `${baseArrow} border-l-[6px] border-l-[#3F3F46] border-y-[6px] border-y-transparent`,
          style: { right: "-6px", top: "50%", transform: "translateY(-50%)" }
        };
      case "right":
        return {
          className: `${baseArrow} border-r-[6px] border-r-[#3F3F46] border-y-[6px] border-y-transparent`,
          style: { left: "-6px", top: "50%", transform: "translateY(-50%)" }
        };
      default:
        return {
          className: `${baseArrow} border-t-[6px] border-t-[#3F3F46] border-x-[6px] border-x-transparent`,
          style: { bottom: "-6px", left: "50%", transform: "translateX(-50%)" }
        };
    }
  };


  const arrow = getArrowStyles();


  const tooltipContent = isVisible && createPortal(
    <div
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: getTransform(),
        zIndex: 9999,
      }}
      className="pointer-events-none"
    >
      <div className="relative">
        <span
          className={`block w-max max-w-[280px] rounded-md bg-[#3F3F46] px-3 py-2 text-[12px] leading-[16px] text-white shadow-lg ${tooltipClassName}`}
        >
          {content}
        </span>
        <div className={arrow.className} style={arrow.style}></div>
      </div>
    </div>,
    document.body
  );


  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {tooltipContent}
    </span>
  );
};


export default Tooltip;
